import { CUST_REQ_STATUS, formatReceivedAt, photoCountText } from '@indipia/shared-features';
import type { CustReqItem } from '@indipia/shared-features';
import type { CustReqDelivered, CustReqRef } from '../types/custReqWorkOrder.types';

// 작업요청 화면(작성·일괄·이력)의 고객 요청(S16) 순수 로직 — 시험: custReqOrder.logic.test.ts.
// 상태·경로 이름은 서버가 준 statusName·srcName 을 그대로 쓴다(하드코딩 금지).

export const CUST_REQ_ORDER_MESSAGE = {
  cardTitle: (n: number) => `고객 요청 ${n}건`,
  cardHint: '체크한 요청이 함께 전달돼요 · 원문 수정은 인디플래너에서',
  excluded: '이번에 빼기 · 접수로 남아요',
  loading: '고객 요청을 불러오는 중입니다.',
  loadError: '고객 요청을 불러오지 못했습니다.',
  retry: '다시 불러오기',
  noContent: '보낼 내용이 없어요',
  reqChecking: '고객 요청을 확인하는 중이에요',
  reqLoadFailed: '고객 요청을 불러오지 못했어요 · 다시 불러온 뒤 보내 주세요',
  contentLabel: '작업 요청내용',
  memoGuide: '공종을 고르면 인디플래너 공종 상세의 작업 요청 메모를 불러와요',
  memoNone: '작업 요청 메모 없음 · 인디플래너 공종 상세에서 입력해요',
  memoLoaded: '작업 요청 메모를 불러왔어요 · 고쳐도 원본은 그대로예요',
  memoEdited: '직접 고친 내용이에요 · 인디플래너 원본은 그대로예요',
  memoReload: '↺ 작업 요청 메모 다시 불러오기',
  conflictFallback: '선택한 고객 요청이 변경되었거나 삭제되었습니다. 목록을 새로고침해 주세요',
  bulkCommonLabel: '모든 공종에 공통으로 덧붙일 내용 (선택)',
  bulkCommonPlaceholder: '선택한 모든 공종의 작업 요청내용 뒤에 덧붙여 전달할 내용을 입력하세요',
  bulkRowOpen: '내용',
  bulkBodyLabel: '이 공종의 작업 요청내용',
  bulkBodyPlaceholder: '이 공종에만 전달할 작업 내용을 입력하세요',
  listToggle: (n: number) => `고객 요청 ${n}`,
  listMeta: '전달 당시 요청과 지금 상태예요',
} as const;

/** 작업요청 발송 대상(접수 상태)만 — 서버가 준 접수 순을 유지 */
export function receivedOnly(items: readonly CustReqItem[] | null | undefined): CustReqItem[] {
  return (items ?? []).filter((it) => it.statusCode === CUST_REQ_STATUS.RECEIVED);
}

/** 체크 해제(이번에 빼기)한 요청을 뺀 발송 대상 */
export function includedOf(items: readonly CustReqItem[], excluded: ReadonlySet<number>): CustReqItem[] {
  return items.filter((it) => !excluded.has(it.custReqId));
}

/** 발송 참조 — 요청 목록 조회가 준 updatedAt 을 그대로 쓴다 */
export function toRefs(items: readonly CustReqItem[]): CustReqRef[] {
  return items.map((it) => ({ custReqId: it.custReqId, updatedAt: it.updatedAt }));
}

/** 체크 토글(제외 집합) — 새 집합을 돌려준다 */
export function toggleExcluded(excluded: ReadonlySet<number>, custReqId: number): Set<number> {
  const next = new Set(excluded);
  if (next.has(custReqId)) next.delete(custReqId);
  else next.add(custReqId);
  return next;
}

export interface MemoApplyResult {
  content: string;
  /** 마지막으로 자동 채운 메모(불러온 그대로 판정 기준) */
  loadedMemo: string;
  replaced: boolean;
}

/**
 * 공종이 바뀔 때 작업 요청내용 규칙(941 S16-6) — 입력란이 비었거나 불러온 그대로면 새 공종 메모로 교체,
 * 직접 고쳤으면 그대로 둔다(다시 불러오기 링크로 교체 가능). 메모가 없으면 빈 칸이 된다.
 */
export function applyMemoOnTaskChange(content: string, loadedMemo: string | null, memo: string | null | undefined): MemoApplyResult {
  const next = memo ?? '';
  if (content.trim() === '' || content === loadedMemo) return { content: next, loadedMemo: next, replaced: true };
  return { content, loadedMemo: loadedMemo ?? '', replaced: false };
}

export type MemoHintKind = 'guide' | 'none' | 'loaded' | 'edited';

/** 입력란 위 안내 3종 + 공종 선택 전 안내 */
export function memoHintKind(taskSelected: boolean, memo: string | null | undefined, content: string): MemoHintKind {
  if (!taskSelected) return 'guide';
  if (!memo || memo.trim() === '') return 'none';
  return content === memo ? 'loaded' : 'edited';
}

export function memoHintText(kind: MemoHintKind): string {
  switch (kind) {
    case 'guide': return CUST_REQ_ORDER_MESSAGE.memoGuide;
    case 'none': return CUST_REQ_ORDER_MESSAGE.memoNone;
    case 'loaded': return CUST_REQ_ORDER_MESSAGE.memoLoaded;
    default: return CUST_REQ_ORDER_MESSAGE.memoEdited;
  }
}

/** '↺ 작업 요청 메모 다시 불러오기' 노출 — 메모가 있고 입력란이 메모와 다를 때 */
export function canReloadMemo(memo: string | null | undefined, content: string): boolean {
  return !!memo && memo.trim() !== '' && content !== memo;
}

/** 단건 작성 발송 조건 — 작업 요청내용 · 고객 요청 중 하나라도 있으면 보낼 수 있다(요청 확인 중·실패는 막음) */
export function canSendSingle(o: {
  hasSite: boolean;
  content: string;
  includedCount: number;
  hasWorker: boolean;
  sending: boolean;
  reqBlocked: boolean;
}): boolean {
  return o.hasSite && (o.content.trim().length > 0 || o.includedCount > 0) && o.hasWorker && !o.sending && !o.reqBlocked;
}

/** 일괄 작성에서 한 공종이 보낼 내용을 가졌는지 — 공종 본문 · 공통 덧붙임 · 포함 요청 중 하나 */
export function rowHasContent(body: string, common: string, includedCount: number): boolean {
  return body.trim() !== '' || common.trim() !== '' || includedCount > 0;
}

/** 일괄 발송 하단 요약 문구 */
export function bulkSummaryText(selectedCount: number, resendCount: number, reqCount: number): string {
  return `${selectedCount}개 선택됨${resendCount > 0 ? ` (이 중 ${resendCount}개는 재발송)` : ''}${reqCount > 0 ? ` · 고객 요청 ${reqCount}건 포함` : ''}`;
}

export function missingContentText(n: number): string {
  return `보낼 내용이 없는 공종 ${n}개`;
}

/** 발송 완료 안내 — 함께 전달된 요청이 없으면 null */
export function deliveredNoticeText(n: number | null | undefined): string | null {
  return typeof n === 'number' && n > 0 ? `고객 요청 ${n}건 함께 전달` : null;
}

/** 일괄 발송 완료 토스트 문구 */
export function bulkDoneText(succeeded: number, failed: number, deliveredReqs: number): string {
  const base = `${succeeded}건 발송되었습니다${failed > 0 ? ` (${failed}건 실패)` : ''}`;
  return deliveredReqs > 0 ? `${base} · 고객 요청 ${deliveredReqs}건 함께 전달` : base;
}

/** 요청 한 줄 보조 문구 — `카톡 · 6/18 10:03 접수 · (사진 1)` (값이 없는 항목은 생략) */
export function reqMetaText(srcName: string, receivedAt: string, photoCount: number): string {
  const at = formatReceivedAt(receivedAt);
  return [srcName, at ? `${at} 접수` : '', photoCountText(photoCount)].filter((s) => s !== '').join(' · ');
}

/** 서버 오류가 고객 요청 전달 정합 충돌(409 CUST_REQ_STATE_CONFLICT)인지 */
export function isCustReqConflict(err: unknown): boolean {
  const r = (err as { response?: { status?: number; data?: { code?: unknown } } } | null)?.response;
  return r?.status === 409 && r?.data?.code === 'CUST_REQ_STATE_CONFLICT';
}

/** 이력 카드 '고객 요청 n ▸' 대상 — 없으면 빈 배열(키가 없는 응답도 안전) */
export function deliveredList(items: readonly CustReqDelivered[] | null | undefined): CustReqDelivered[] {
  return items ? [...items] : [];
}
